"use client"
import type {
  ResultOf,
  SortDirection,
  TopCollectionsSortBy,
} from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import { UnreachableCaseError } from "@opensea/utils"
import { set } from "es-toolkit/compat"
import type { Dispatch, SetStateAction } from "react"
import { NativePriceFragment } from "@/components/price/NativePrice.fragment"
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment"
import { VolumeFragment } from "@/components/price/Volume.fragment"
import type { Value } from "@/lib/pagination/pagination-utils"
import { Paginator } from "@/lib/pagination/paginator"
import { useSubscription } from "../use-subscription"

export const CollectionStatsSubscriptionFragment = graphql(
  `
    fragment CollectionStatsSubscription on Collection {
      id
      slug
      __typename
      floorPrice {
        pricePerItem {
          usd
          ...TokenPrice
          ...NativePrice
        }
      }
      topOffer {
        pricePerItem {
          usd
          ...TokenPrice
          ...NativePrice
        }
      }
      stats {
        ownerCount
        totalSupply
        uniqueItemCount
        listedItemCount
        volume {
          usd
          ...Volume
        }
        sales
        oneMinute {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        fiveMinute {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        fifteenMinute {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        oneHour {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        oneDay {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        sevenDays {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
        thirtyDays {
          floorPriceChange
          sales
          volume {
            usd
            ...Volume
          }
        }
      }
    }
  `,
  [VolumeFragment, NativePriceFragment, TokenPriceFragment],
)

const subscription = graphql(
  `
    subscription useCollectionStatsSubscription($slugs: [String!]!) {
      collectionsBySlugs(slugs: $slugs) {
        __typename
        ... on DelistedCollection {
          id
        }
        ... on BlacklistedCollection {
          id
        }
        ... on Collection {
          id
          slug
          ...CollectionStatsSubscription
        }
      }
    }
  `,
  [CollectionStatsSubscriptionFragment],
)

export type UseCollectionStatsSubscriptionResponse = ResultOf<
  typeof subscription
>

type Sort = {
  by: TopCollectionsSortBy
  direction: SortDirection
}
type CollectionResult =
  UseCollectionStatsSubscriptionResponse["collectionsBySlugs"]

class CollectionStatsPaginator extends Paginator<
  CollectionResult,
  TopCollectionsSortBy
> {
  protected getValues(
    item: CollectionResult,
    sortBy: TopCollectionsSortBy | undefined,
  ): Value[] {
    if (isDelistedCollection(item)) {
      return []
    }
    if (isBlacklistedCollection(item)) {
      return []
    }

    const element = readFragment(CollectionStatsSubscriptionFragment, item)

    switch (sortBy) {
      case "FLOOR_PRICE":
        return [element.floorPrice?.pricePerItem.usd ?? 0, element.id]
      case "VOLUME":
        return [element.stats.volume.usd, element.id]
      case "SALES":
        return [element.stats.sales, element.id]
      case "TOP_OFFER":
        return [element.topOffer?.pricePerItem.usd ?? 0, element.id]
      case "ONE_MINUTE_VOLUME":
        return [element.stats.oneMinute.volume.usd, element.id]
      case "ONE_MINUTE_SALES":
        return [element.stats.oneMinute.sales, element.id]
      case "ONE_MINUTE_FLOOR_PRICE_CHANGE":
        return [element.stats.oneMinute.floorPriceChange ?? 0, element.id]
      case "FIVE_MINUTE_VOLUME":
        return [element.stats.fiveMinute.volume.usd, element.id]
      case "FIVE_MINUTE_SALES":
        return [element.stats.fiveMinute.sales, element.id]
      case "FIVE_MINUTE_FLOOR_PRICE_CHANGE":
        return [element.stats.fiveMinute.floorPriceChange ?? 0, element.id]
      case "FIFTEEN_MINUTE_VOLUME":
        return [element.stats.fifteenMinute.volume.usd, element.id]
      case "FIFTEEN_MINUTE_SALES":
        return [element.stats.fifteenMinute.sales, element.id]
      case "FIFTEEN_MINUTE_FLOOR_PRICE_CHANGE":
        return [element.stats.fifteenMinute.floorPriceChange ?? 0, element.id]
      case "ONE_HOUR_VOLUME":
        return [element.stats.oneHour.volume.usd, element.id]
      case "ONE_HOUR_SALES":
        return [element.stats.oneHour.sales, element.id]
      case "ONE_HOUR_FLOOR_PRICE_CHANGE":
        return [element.stats.oneHour.floorPriceChange ?? 0, element.id]
      case "ONE_DAY_SALES":
        return [element.stats.oneDay.sales, element.id]
      case "ONE_DAY_VOLUME":
        return [element.stats.oneDay.volume.usd, element.id]
      case "ONE_DAY_FLOOR_PRICE_CHANGE":
        return [element.stats.oneDay.floorPriceChange ?? 0, element.id]
      case "SEVEN_DAYS_VOLUME":
        return [element.stats.sevenDays.volume.usd, element.id]
      case "SEVEN_DAYS_SALES":
        return [element.stats.sevenDays.sales, element.id]
      case "SEVEN_DAYS_FLOOR_PRICE_CHANGE":
        return [element.stats.sevenDays.floorPriceChange ?? 0, element.id]
      case "THIRTY_DAYS_VOLUME":
        return [element.stats.thirtyDays.volume.usd, element.id]
      case "THIRTY_DAYS_SALES":
        return [element.stats.thirtyDays.sales, element.id]
      case "THIRTY_DAYS_FLOOR_PRICE_CHANGE":
        return [element.stats.thirtyDays.floorPriceChange ?? 0, element.id]
      case undefined:
        return [element.id]
      default:
        throw new UnreachableCaseError(sortBy)
    }
  }
}

const collectionStatsPaginator = new CollectionStatsPaginator({
  // no limit ~ if we want to limit the table, we need to do it on the pagination level as well
  // to avoid infinite reloading
  maxItems: undefined,
})

const MAX_COLLECTIONS_TO_LIVE_UPDATE = 200

export const useCollectionsStatsSubscription = <C extends CollectionResult>(
  items: C[],
  setItems: Dispatch<SetStateAction<C[]>>,
  sort?: Sort,
) => {
  const slugs = items
    .map(getSlug)
    .filter(isDefined)
    .sort((a, b) => a.localeCompare(b))
    .slice(0, MAX_COLLECTIONS_TO_LIVE_UPDATE)

  useSubscription(
    {
      query: subscription,
      variables: { slugs },
    },
    msg => {
      const item = msg.collectionsBySlugs
      setItems(prev => {
        const updated = collectionStatsPaginator.updateInOrder(
          prev,
          sort,
          element => getSlug(element) === getSlug(item),
          element => {
            if (isDelistedCollection(item)) {
              return element
            }
            if (isBlacklistedCollection(item)) {
              return element
            }
            const collection = readFragment(
              CollectionStatsSubscriptionFragment,
              item,
            )
            return set(element, "stats", collection.stats)
          },
        ) as C[]
        return updated
      })
    },
  )
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}

const getSlug = <C extends CollectionResult>(item: C): string | undefined => {
  if (isDelistedCollection(item)) {
    return
  }
  if (isBlacklistedCollection(item)) {
    return
  }
  return item.slug
}

function isDelistedCollection(item: CollectionResult) {
  return item.__typename === "DelistedCollection"
}

function isBlacklistedCollection(item: CollectionResult) {
  return item.__typename === "BlacklistedCollection"
}
